import { useTranslate } from '@tolgee/react';
import { Button, TableColumnsType, TableProps, Tooltip } from 'antd';
import ScrollTable from 'components/ScrollTable';
import { ReactNode, useMemo } from 'react';
import { formatNumber } from 'utils/numberFormat';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { What } from 'views/Aide/Scenario/PolicyTable';
import { durationDisplay } from '../ImplementData/utils';
import { Invalid } from './ShopeePerformanceTable';

type RT = Dashboard.PlatformData.StorePerfData;

function LazadaPerformanceTable(
  props: TableProps<RT> & {
    onCheckTrend: (record: RT) => void;
  },
) {
  const { dataSource: dataSourceProp, onCheckTrend, ...otherProps } = props;

  const dataSource = useMemo(() => {
    return dataSourceProp?.map(({ data, ...others }) => {
      try {
        const jsonData = (JSON.parse(data) as any[]).reduce<any>((res, cur) => {
          res[cur.type] = cur.score;
          return res;
        }, {});
        return {
          ...others,
          ...jsonData,
        };
      } catch (_) {
        return others;
      }
    });
  }, [dataSourceProp]);

  const { t } = useTranslate();

  const invalidRender = (node: ReactNode, invalid: boolean) =>
    invalid ? (
      <Invalid>
        {node}
        <span>
          <ExclamationCircleFilled />
          {t('异常')}
        </span>
      </Invalid>
    ) : (
      node
    );

  const columns: TableColumnsType<any> = [
    {
      key: 'region',
      dataIndex: 'region',
      title: t('站点'),
      width: 84,
    },
    {
      key: 'storeAlias',
      dataIndex: 'storeAlias',
      title: t('ViewShops.店铺别名'),
      width: 190,
      render: (alias: RT['storeAlias']) => alias || '-',
    },
    {
      key: 'storeName',
      dataIndex: 'storeName',
      title: t('店铺名称'),
      width: 190,
      render: (name: RT['storeName']) => name || '-',
    },
    {
      key: 'POSITIVE_SELLER_RATING',
      dataIndex: 'POSITIVE_SELLER_RATING',
      title: (
        <div>
          {t('买家好评率')}
          <Tooltip title={t('买家给予店铺的好评数/总评价数')}>
            <What />
          </Tooltip>
        </div>
      ),
      width: 164,
      render: (rating) => formatNumber(rating / 100, '0.0%'),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      key: 'CHAT_RESPONSE_RATE',
      dataIndex: 'CHAT_RESPONSE_RATE',
      title: (
        <div>
          {t('回复率')}
          <Tooltip
            title={t(
              '过去30天（节假日除外）回复率的平均值，当地时间n-1天18:00到n天18:00的咨询算作第n天的咨询，其中客服在n天19:00前有回复的消息算在回复率内。',
            )}
          >
            <What />
          </Tooltip>
        </div>
      ),
      width: 160,
      render: (rate) => invalidRender(formatNumber(rate / 100, '0.0%'), rate < 90),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      key: 'CHAT_RESPONSE_TIME',
      dataIndex: 'CHAT_RESPONSE_TIME',
      title: (
        <div>
          {t('平均响应时长')}
          <Tooltip title={t('卖家侧首次回复客户的平均时长')}>
            <What />
          </Tooltip>
        </div>
      ),
      width: 215,
      render: (time) => invalidRender(durationDisplay(time * 60), time > 60),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      key: 'operate',
      title: t('操作'),
      width: 165,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" onClick={() => onCheckTrend(record)}>
          {t('查看趋势')}
        </Button>
      ),
    },
  ];

  return <ScrollTable dataSource={dataSource} columns={columns} {...otherProps} />;
}

export default LazadaPerformanceTable;
